import * as THREE from 'three/webgpu';
import type { PlacementPose } from '../../world/collision';
import { placementQuat } from '../../world/collision';
// The ONE shadow-flag applier and the class column a batched `static_mesh` placement would have
// carried unbatched. See the call site for why a batch must re-declare after `root.add`.
import { applyShadowFlags } from '../lights/shadowFlags';
import { ENTITY_SHADOW } from '../lights/lightModel';

/**
 * ⭐ THE STATIC BATCHER — ONE `InstancedMesh` PER (ASSET, PRIMITIVE, CELL, WINDING).
 *
 * ── WHY, AND IT IS NOT THE REASON THE ROADMAP GIVES ─────────────────────────────────────────────
 *
 * A CDP profile of `mars-hangar-01` (5,563 placements, 177 meshes) put a 45 ms CPU frame against a
 * **2.2 ms GPU** — the device asleep — with the two largest entries being PER-OBJECT costs:
 * `_projectObject` 17.6 % and `updateMatrixWorld` 13.9 %, against ~16 % for all per-DRAW work
 * combined. `_projectObject` does a frustum sphere test, two `Matrix4` applications for the depth
 * sort and a render-list push **per object, per pass**, and `frameCalls` is 3.
 *
 * So the problem is not how many draw commands we issue. It is **how many objects three has to
 * think about to issue them**. The roadmap evaluates batching by "does it collapse draw calls" and
 * correctly answers no on WebGPU — `three.webgpu.js` is a JS loop of `drawIndexed` — but that is
 * the wrong question for this profile, and asking it hid the answer.
 *
 * ── WHY `InstancedMesh` AND NOT `BatchedMesh` ───────────────────────────────────────────────────
 *
 * Read out of our pinned `three@0.185.1`, not from documentation:
 *
 *   · `BatchedMesh.js` sets `perObjectFrustumCulled = true` (:211) and loops its instances in BOTH
 *     `onBeforeRender` (:1518) and `onBeforeShadow` (:1682) — per-instance JS, in every pass, and
 *     the passes are exactly where we are already too slow.
 *   · `InstancedMesh.js` has **no** `onBeforeRender`, **no** `onBeforeShadow` and **no**
 *     per-instance culling. One object, one frustum test, one draw with `instanceCount`.
 *
 * `BatchedMesh` is the tool for HETEROGENEOUS geometry sharing a material — an HLOD proxy, a merged
 * prop cell. Our content is the other case entirely: one geometry placed many times. `SM_L_Wall`
 * ×437, `SM_L_Lean_Wall` ×411, ten meshes covering 44 % of all placements.
 *
 * ── THE KEY HAS FOUR TERMS AND EVERY ONE OF THEM IS FORCED ──────────────────────────────────────
 *
 * `asset` and `primitive` are obvious — an `InstancedMesh` is one geometry and one material.
 * The other two are not, and each is a bug if omitted:
 *
 * ⚠️ **CELL.** An `InstancedMesh` is culled as ONE unit. Batch `SM_L_Wall`'s 437 placements
 * together and that batch spans the whole 688 m level, so it is always on screen and every instance
 * is submitted always — where today those 437 objects cull individually. Including the cell in the
 * key means a batch is cell-sized and culls like one. This is why cells are not a later stage:
 * adding them afterwards means shipping the regression first and repairing it, and `CELL_M = ∞`
 * reproduces the no-cell case exactly for measurement.
 *
 * ⚠️ **WINDING.** 627 of Mars's placements have a NEGATIVE DETERMINANT and are genuinely mirrored.
 * Winding is a property of the render PIPELINE, not of an instance, so a mirrored instance sharing
 * a batch with an unmirrored one renders inside-out. They get their own batch — and HOW that batch
 * states its mirror is the subject of {@link MIRROR}, which is where the black faces came from.
 * This is F2 arriving downstream exactly where it was predicted to.
 */

/**
 * Cell size in metres. Placements are keyed by their integer cell so a batch is a local cluster.
 *
 * ⚠️ NOT TUNED — a starting value, and the whole point of it being a constant here is that the
 * profile chooses it, not this comment. Too large and batches stop culling (the failure this term
 * exists to prevent); too small and instance counts collapse toward 1, which is every cost of a
 * draw call with none of the benefit of instancing. `Infinity` disables cells entirely and is the
 * honest baseline to measure against.
 */
export const DEFAULT_CELL_M = 64;

/**
 * ⭐ THE CELL IS A CULLING DECISION, SO IT IS PRICED PER GROUP AND NOT TAKEN AS A CONSTANT.
 *
 * `DEFAULT_CELL_M`'s own comment says the profile should choose it. The profile did, and the answer
 * is that **there is no single right value**, because the two sides of the trade are not properties
 * of the world — they are properties of each ASSET. Measured on `mars-hangar-01`, fully bound, one
 * arm per cell size, on the gameplay camera:
 *
 * | cell   | drawables | our JS | frame gap | blocked (GPU) |
 * |--------|-----------|--------|-----------|---------------|
 * | 64 m   | 1,321     | 55.8   | 56.8      | 1.0 (2 %)     |
 * | 128 m  | 784       | 37.6   | 41.3      | 3.7 (9 %)     |
 * | 256 m  | 541       | 32.0   | 40.0      | 8.0 (20 %)    |
 * | ∞      | 228       | 20.9   | 40.2      | 19.3 (48 %)   |
 *
 * Two straight lines cross: **our CPU is ~31.9 µs per drawable per frame** (fit over those four
 * points: `cpu ≈ 13.7 ms + 0.0319 · drawables`), and coarsening buys that back by DELETING THE
 * CULL, which took triangles 5.6 M → 17.2 M and put the GPU into the frame instead. Coarser is
 * strictly better on CPU and strictly worse on GPU, and the wall is `max(cpu, gpu)` — so a single
 * constant can only pick a point on that curve, and its floor here is ~40 ms.
 *
 * ⚠️ **BUT THE TWO COSTS DO NOT LAND ON THE SAME ASSETS.** 1,144 of the 1,321 batches at 64 m held
 * EIGHT INSTANCES OR FEWER and the median held **two** — those are rare props, and merging them
 * costs almost no triangles because there are almost no instances to stop culling. The triangles
 * are in the other tail: `SM_L_Wall` ×437 spanning a 688 m level, where the cell is doing real work.
 *
 * So the rule is a price, not a size: **split a group into cells only while a cell is worth
 * culling.** A batch that will never carry more than `MIN_CULL_TRIS` triangles cannot save more GPU
 * time than the extra draw costs CPU time, so it should not exist.
 *
 * The number is derived from the table above rather than chosen: an extra drawable costs ~31.9 µs
 * of CPU per frame, and a triangle costs ~1.7 ns of GPU (blocked went 1.0 → 19.3 ms for +11.6 M
 * triangles). Break-even is `31.9 µs / 1.7 ns ≈ 18,700` triangles that the split actually culls —
 * and a batch is only off-screen some of the time, so the batch itself must be bigger than that.
 * `24_000` is that break-even with a modest margin, and `?minculltris=` is how it was swept.
 *
 * ⚠️ It is a HEURISTIC OVER A MEASURED CONSTANT, and both halves of the constant are this machine's
 * (an Intel gen-12lp iGPU). On a discrete GPU the triangle gets cheaper and the right answer moves
 * TOWARD merging; on a school tablet it moves the other way. It is a per-frame cost model, so it is
 * reported (`__fwg.state().counts.instanced*`) rather than trusted.
 */
export const MIN_CULL_TRIS = 24_000;

/** How many times the base cell may double before a group is simply one batch. 64 m → 16 km. */
const MAX_COARSEN_STEPS = 8;

/**
 * The `?cell=` dev override's vocabulary, PURE so the sweep that chooses `DEFAULT_CELL_M` and the
 * boot that reads the URL agree by construction.
 *
 * `null` (absent) ⇒ the authored default. `0` / `inf` / `infinity` / `none` ⇒ no cells, which is
 * the baseline arm. A positive finite number ⇒ that grain. Anything else is REFUSED to the default
 * with a warning rather than throwing (rule 6) — a mistyped measurement flag must not brick a boot,
 * and it must not silently become a different measurement either.
 */
export function resolveCellM(raw: string | null): number {
  if (raw === null || raw === '') return DEFAULT_CELL_M;
  const word = raw.trim().toLowerCase();
  if (word === 'inf' || word === 'infinity' || word === 'none' || word === '0') return Infinity;
  const n = Number(word);
  if (Number.isFinite(n) && n > 0) return n;
  console.warn(`[batch] ?cell=${raw} is not a size — using the authored ${DEFAULT_CELL_M} m`);
  return DEFAULT_CELL_M;
}

/** The same lenient reader for `?minculltris=`. `0` disables pricing (every group keeps the base cell). */
export function resolveMinCullTris(raw: string | null): number {
  if (raw === null || raw === '') return MIN_CULL_TRIS;
  const n = Number(raw.trim());
  if (Number.isFinite(n) && n >= 0) return n;
  console.warn(`[batch] ?minculltris=${raw} is not a count — using the authored ${MIN_CULL_TRIS}`);
  return MIN_CULL_TRIS;
}

/** A world position, only the axes the cell key uses. */
export interface CellPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * ⭐ THE PRICE — how coarse this group's cells have to be before a cell is worth culling. PURE, and
 * separated from the batcher so the choice can be held to arithmetic in a test rather than to a
 * screenshot of a frame.
 *
 * Coarsens by DOUBLING from the base cell while the mean batch carries fewer than `minCullTris`
 * triangles, and gives up to one batch (`Infinity`) rather than pretend a 16 km cell means anything.
 * The mean, not the minimum: one sparse outlying cell must not force the whole group to merge, and
 * one dense cell must not license the rest to stay fragmented.
 *
 * `trisPerInstance` of 0 (a geometry with no position attribute — it can happen, and it draws
 * nothing) prices as worthless to cull, which is the correct answer rather than a divide by zero.
 */
export function priceCellM(
  points: readonly CellPoint[],
  trisPerInstance: number,
  baseCellM: number,
  minCullTris: number,
): number {
  if (points.length === 0) return baseCellM;
  if (!Number.isFinite(baseCellM)) return Infinity;
  if (minCullTris <= 0) return baseCellM;
  const totalTris = points.length * trisPerInstance;
  // Nothing here is worth culling in pieces — one batch, whatever the level's extent.
  if (totalTris < minCullTris) return Infinity;
  let cell = baseCellM;
  for (let step = 0; step <= MAX_COARSEN_STEPS; step++) {
    const cells = new Set<string>();
    for (const p of points) {
      cells.add(`${Math.floor(p.x / cell)},${Math.floor(p.y / cell)},${Math.floor(p.z / cell)}`);
    }
    if (cells.size <= 1) return Infinity;
    if (totalTris / cells.size >= minCullTris) return cell;
    cell *= 2;
  }
  return Infinity;
}

/** What a placement contributes: one instance of one primitive of one asset. */
interface Instance {
  matrix: THREE.Matrix4;
  /** The world's placement index — what the editor selects and what `pick` returns. */
  placement: number;
  /** The placement's own position, kept so `seal()` can re-bucket without decomposing a matrix. */
  at: CellPoint;
}

interface Batch {
  /** `asset|primitive|winding` — the batch's GROUP, i.e. everything in its key but the cell. */
  group: string;
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  mirrored: boolean;
  instances: Instance[];
  /** Allocated instance slots — the mesh is only rebuilt when this is exceeded (see flush). */
  capacity: number;
  mesh: THREE.InstancedMesh | null;
}

/**
 * One `(asset, primitive, winding)` group: the unit the CELL SIZE is priced for. A group's batches
 * are its instances partitioned by `cellM`; `seal()` is the one thing that may change that number.
 */
interface Group {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  mirrored: boolean;
  /** Triangles in one instance — the currency `priceCellM` spends. */
  tris: number;
  /** The cell the group's live batches were built with. */
  cellM: number;
  keys: Set<string>;
}

/** Triangles one draw of this geometry submits — the indexed count where there is one. */
function triangleCount(geometry: THREE.BufferGeometry): number {
  const index = geometry.getIndex();
  if (index !== null) return Math.floor(index.count / 3);
  const pos = geometry.getAttribute('position');
  return pos === undefined ? 0 : Math.floor(pos.count / 3);
}

/** One asset's drawable primitives, extracted once from the shared (already stripped) scene. */
interface AssetPrimitives {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  /** The primitive's own transform inside the GLB, folded into every instance matrix. */
  local: THREE.Matrix4;
}

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();

export interface StaticBatcher {
  /**
   * Offer a placement to the batcher. Returns true when it was taken, in which case the caller must
   * NOT parent the geometry itself — the batch draws it.
   *
   * `scene` is the SHARED, already-stripped asset scene (`staticMesh`'s cache), never a clone: the
   * batcher reads its geometry and material by reference, which is the entire memory argument.
   */
  add(assetId: string, scene: THREE.Object3D, pose: PlacementPose, placement?: number): boolean;
  /**
   * Build or rebuild the `InstancedMesh`es for everything added since the last flush.
   *
   * Called automatically, coalesced, one tick after the last `add` — placements arrive over many
   * ticks as their GLBs land, and rebuilding a batch per arrival would be quadratic over 5,563 of
   * them. Exposed anyway so a test can be synchronous about it.
   */
  flush(): void;
  /**
   * ⭐ PRICE EVERY GROUP'S CELL, NOW THAT EVERY PLACEMENT HAS ARRIVED.
   *
   * `add()` cannot make this decision: the right cell for a group is a function of the WHOLE group
   * (see `priceCellM`), and while placements are streaming in, every group looks small. So the
   * streaming path keeps the authored cell — today's behaviour exactly, with today's incremental
   * grow-in-place — and this runs ONCE, from mount's `onAllLoaded`, before `warmup()` compiles.
   *
   * That ordering is the whole reason it is a separate verb. Re-partitioning after the warm-up
   * would throw away the pipelines the warm-up just compiled and pay for them again on the first
   * frame the player sees, which is the mistake `flush()`-before-`warmup()` already exists to
   * avoid — the same trap, one step later.
   *
   * Only groups whose priced cell DIFFERS from the one they were built with are rebuilt, so the
   * dense assets that were right at 64 m are not disturbed at all.
   */
  seal(): void;
  /** `instanceId` on a batch → the world placement index, for editor picking. */
  pick(mesh: THREE.Object3D, instanceId: number): number | null;
  /** Drop every batch and free the GPU resources this module created (never the shared geometry). */
  clear(): void;
  stats(): { batches: number; instances: number; assets: number; sealed: boolean };
}

/*
 * ── ⭐ THE MIRROR LIVES ON THE BATCH'S OWN MATRIX, NOT ON A FLIPPED `side` ───────────────────────
 *
 * A mirrored batch has to state its mirror somewhere three will act on, and there are exactly two
 * places: the MATERIAL's `side`, or the OBJECT's own world matrix. The first is what this file did,
 * and it was measured to be **5x the black pixels of the unbatched arm** on `mars-hangar-01`.
 *
 * ⚠️ **`side = BackSide` CARRIES A SECOND PAYLOAD, AND IT IS A SHADING PAYLOAD.** It was reached
 * for because it does the one thing wanted — `WebGPUPipelineUtils._getPrimitiveState` computes
 * `flipSided = ( material.side === BackSide )` and sets `frontFace = CW` (r185 `:848-852`;
 * `WebGLState.setMaterial` `:909` is the same line on the fallback backend), so the mirrored
 * triangles stop being culled. But the SAME enum is read in the fragment graph:
 * `negateOnBackSide` (r185 `FrontFacingNode.js:91-107`) multiplies the shading normal by `-1.0`
 * unconditionally when `side === BackSide`, and `normalView` applies it (`Normal.js:104`).
 *
 * And that negation is not wanted, because the normal is ALREADY correct: `Instance.js:236` runs
 * `transformNormal( normalLocal, instanceMatrix )`, which is `inverse(mat3(M))ᵀ · n` normalized
 * (`Normal.js:164-168`) — the covector transform, which is exactly the outward normal of the
 * mirrored surface. Negating it points every mirrored fragment's normal INTO its own surface, so
 * `N·L ≤ 0` for every light: **a black face.** 521 of Mars's 627 mirrored placements are flipped in
 * Y alone, and a Y mirror leaves a vertical wall's horizontal normal UNCHANGED — so for those the
 * `side` flip is a pure, unopposed sign error.
 *
 * ⭐ **THE OBJECT MATRIX IS THE OTHER PLACE, AND IT IS THE ONE THE UNBATCHED PATH ALREADY USES.**
 * Both backends flip the winding for a negative-determinant OBJECT, and both put that fact in the
 * pipeline cache key so two batches sharing one material still get two pipelines:
 *
 *   `if ( object.isMesh && object.matrixWorld.determinantAffine() < 0 ) flipSided = ! flipSided;`
 *      — `WebGPUPipelineUtils.js:850`, `WebGLBackend.js:1083`, `WebGPUBackend.js:2123` (the key)
 *
 * That is precisely why `?batch=0` never showed the artefact: an unbatched placement carries its
 * negative scale on its own `Group` (`entities/staticMesh.ts` — `group.scale.set(...scaleVec3())`),
 * so the determinant is on `matrixWorld`, three flips the winding itself, and `side` is never
 * touched. **An `InstancedMesh`'s per-instance matrix reaches neither line** — `object.matrixWorld`
 * is the batch's, not the instance's — which is the whole reason a workaround was needed at all.
 *
 * So a mirrored batch takes the mirror ONTO ITS OWN MATRIX and each instance matrix is
 * pre-multiplied by the mirror's inverse. `MIRROR` is an involution (`MIRROR⁻¹ === MIRROR`), so the
 * composed world transform of every instance is byte-identical to what it was:
 *
 *      world = MIRROR · (MIRROR · placement · local) = placement · local
 *
 * and the three consequences are each what was wanted:
 *   - `matrixWorld.determinantAffine() < 0` ⇒ three flips the winding, on both backends;
 *   - the instance matrix's determinant is now POSITIVE ⇒ `transformNormal` is a plain rotation
 *     there, and the mirror's covector transform happens once, in `modelNormalMatrix`;
 *   - `side` is untouched ⇒ no `negateOnBackSide`, and no material clone at all.
 *
 * The last of those is a bonus rather than the point: the mirrored twin material is GONE, so a
 * mirrored batch shares its unmirrored twin's WGSL and its `getMaterialCacheKey` (r185
 * `RenderObject.js:752` hashes `side` — *"an enum that changes code"*), and only the pipeline forks
 * on `frontFaceCW`. One shader compile where there were two.
 *
 * ⚠️ Any axis would do; X is chosen because `scale.set(-1, 1, 1)` composes to it exactly under
 * `Object3D.updateMatrix()`, with no quaternion and no floating-point residue.
 */
const MIRROR_SCALE = new THREE.Vector3(-1, 1, 1);
const MIRROR = new THREE.Matrix4().makeScale(-1, 1, 1);

/*
 * ── ⭐⭐ THREE APPLIES THE INSTANCE MATRIX TO **POSITION AND NORMAL ONLY** ────────────────────────
 *
 * This is the rule the whole file has to be read against, and it is `Instance.js`'s own header:
 * *"Transforms positionLocal and normalLocal, and assigns varying color in-place"* (r185 `:116`).
 * `positionLocal` at `:213`, `normalLocal` at `:234-237`, and **nothing else**.
 *
 * Batching MOVES a placement's transform out of the object matrix and into the instance matrix. So
 * every quantity the shader derives from OBJECT space and three does not re-derive per instance is
 * silently left in ASSET space — correct unbatched, wrong batched, with nothing that reports it.
 * Today there is exactly one such quantity and it is the **TANGENT**:
 *
 *     tangentView = modelViewMatrix.mul( vec4( tangentLocal, 0 ) ).xyz   ← `Tangent.js:36`
 *
 * `modelViewMatrix` is the BATCH's, and `tangentLocal` is the raw `attribute('tangent')`. So on a
 * batch the normal is rotated by the placement and the tangent is not, `bitangentView` is
 * `normalView.cross( tangentView )` of those two (`Bitangent.js:57`), and the TBN handed to
 * `NormalMapNode` is three mutually inconsistent axes. Every normal-mapped surface is then shaded
 * through a basis that has nothing to do with its own orientation.
 *
 * ⭐ **MEASURED — THIS IS THE BLACK.** `mars-hangar-01`, `gi=off`, 24 fixed orientations: batched
 * **16.79 %** very-dark against `?batch=0`'s **11.66 %**. Every other candidate was eliminated by
 * measurement first: the geometry and albedo are identical (`unlit`), the shader's own
 * `normalWorld` and `positionWorld` are BIT-IDENTICAL when painted as the frame, the CSM
 * configuration matches, and the gap SURVIVES `sun.castShadow = false` (4.92 pp with `frameCalls`
 * 5 -> 2). All 177 of Mars's cooked GLBs carry a `TANGENT` attribute, and every black pixel sampled
 * landed on `mat-mi-martian-concrete` — which has a `normalMap`, i.e. the one material property
 * that makes the TBN reachable at all.
 *
 * ⭐ **THE FIX IS TO STOP HAVING A TANGENT ATTRIBUTE, NOT TO ROTATE ONE.** three already has an
 * instance-correct path and takes it whenever the geometry has no `tangent`: `tangentViewFrame`
 * (`TangentUtils.js` — Schüler's "Normal Mapping Without Precomputed Tangents"), built from
 * `positionView.dFdx/dFdy` and `uv.dFdx/dFdy` against `normalView`. Every one of those is a
 * POST-INSTANCE quantity, so the frame is correct for any instance transform by construction, and
 * both `tangentView` and `bitangentView` switch to it on the same
 * `builder.geometry.hasAttribute( 'tangent' )` test. No novel TSL (hard rule 3), no material
 * clone, no second normal path.
 *
 * The two alternatives were priced and refused:
 *   · Rotating the tangent needs a custom `normalNode` — novel TSL in the one place this repo
 *     names as its #1 hallucination hotspot, on a term whose failure mode is a picture.
 *   · Putting the ROTATION on the object matrix (as {@link MIRROR} does with the winding) is
 *     correct and unaffordable: Mars has **150 distinct rotations across 176 assets, 796 distinct
 *     (asset, rotation) pairs against today's 278 batches** — roughly 3x the objects, which is the
 *     `_projectObject` cost this whole module exists to remove.
 *
 * ⚠️ **THE VIEW SHARES ITS ATTRIBUTES AND MUST THEREFORE NEVER BE DISPOSED.** `Geometries.js`'s
 * `onDispose` (r185 `:185-210`) does `this.attributes.delete( … )` for the index and EVERY
 * attribute of the disposed geometry — and these are the SAME `BufferAttribute` objects the source
 * geometry draws with. Disposing a view would free the buffers the editor's own clones are still
 * using: the freed-resource class this file already warns about for the shared geometry. It holds
 * no GPU resource of its own; the asset cache owns them all.
 *
 * ⚠️ **AND IT IS A LOOK CHANGE, DECLARED RATHER THAN HIDDEN.** A derived frame is not the authored
 * one — it differs where UVs are mirrored or heavily skewed — so a normal-mapped surface is shaded
 * through a slightly different basis in PLAY (batched, derived) than in the EDITOR (unbatched,
 * authored). That is a real second answer and the honest fix for it is at COOK time: drop `TANGENT`
 * from the cooked GLB and both paths take the derived frame, for 4 fewer floats per vertex on the
 * wire. Not taken here because it is a re-cook of 177 files and a world re-emit, and this stage is
 * the batcher's. Flagged for the Friday hands-on: it is a FEEL/look change.
 */
const tangentFreeViews = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();
function batchGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  if (src.getAttribute('tangent') === undefined) return src;
  const hit = tangentFreeViews.get(src);
  if (hit) return hit;
  const view = new THREE.BufferGeometry();
  // BY REFERENCE — one upload, shared with the source and with every other batch of this asset.
  for (const [name, attr] of Object.entries(src.attributes)) {
    if (name !== 'tangent') view.setAttribute(name, attr);
  }
  const index = src.getIndex();
  if (index !== null) view.setIndex(index);
  for (const g of src.groups) view.addGroup(g.start, g.count, g.materialIndex);
  // Carried over rather than recomputed: they are facts about the positions, which are the same
  // objects. `InstancedMesh.computeBoundingSphere` unions the instances on top of these.
  if (src.boundingBox !== null) view.boundingBox = src.boundingBox.clone();
  if (src.boundingSphere !== null) view.boundingSphere = src.boundingSphere.clone();
  view.name = `${src.name || 'geometry'}#no-tangent`;
  tangentFreeViews.set(src, view);
  return view;
}

export function createStaticBatcher(
  root: THREE.Object3D,
  cellM = DEFAULT_CELL_M,
  minCullTris = MIN_CULL_TRIS,
): StaticBatcher {
  const batches = new Map<string, Batch>();
  const groups = new Map<string, Group>();
  const primitives = new Map<string, AssetPrimitives[]>();
  /** Batches whose instance list changed since the last flush — rebuilt, the rest left alone. */
  const dirty = new Set<string>();
  const meshToBatch = new WeakMap<THREE.Object3D, Batch>();
  let flushScheduled = false;
  let sealed = false;

  const cellKeyOf = (at: CellPoint, size: number): string =>
    Number.isFinite(size)
      ? `${Math.floor(at.x / size)},${Math.floor(at.y / size)},${Math.floor(at.z / size)}`
      : '*';

  /** Create the batch record for `key` inside `group`, or return the one that is already there. */
  const batchFor = (key: string, groupKey: string, group: Group): Batch => {
    let batch = batches.get(key);
    if (batch) return batch;
    batch = {
      group: groupKey,
      geometry: group.geometry,
      material: group.material,
      mirrored: group.mirrored,
      instances: [],
      capacity: 0,
      mesh: null,
    };
    batches.set(key, batch);
    group.keys.add(key);
    return batch;
  };

  /**
   * An asset's drawable primitives, once. The scene is the shared one, so the geometry and material
   * references here are the SAME objects every batch of this asset uses — which is what makes 437
   * walls one upload.
   */
  const primitivesOf = (assetId: string, scene: THREE.Object3D): AssetPrimitives[] => {
    const hit = primitives.get(assetId);
    if (hit) return hit;
    const out: AssetPrimitives[] = [];
    /*
     * ── ⭐ `updateMatrixWorld(true)` ALONE COMPOSES NOTHING HERE, AND IT COST SPONZA ITS ATLAS ────
     *
     * `Object3D.DEFAULT_MATRIX_AUTO_UPDATE = false` repo-wide (`presentation/scene.ts`), and this
     * scene is a FRESHLY PARSED, UNPARENTED GLB — nothing has run the static/dynamic matrix policy
     * over it yet, because the policy is attached by `entityRoot`'s `childadded` hook and a batched
     * placement never parents anything. So every node here is still exactly as GLTFLoader left it,
     * and for a node spelled with TRS (`translation`/`rotation`/`scale` — what every ordinary
     * exporter emits) the loader writes `node.position/quaternion/scale` and NEVER `node.matrix`.
     * `Object3D.updateMatrixWorld()` skips `updateMatrix()` when `matrixAutoUpdate` is false, so it
     * happily composes world matrices out of a tree of IDENTITY locals and **every node transform
     * in the file is silently dropped.**
     *
     * MEASURED on `sponza-debug-01`, whose GLB is one root node carrying `scale: 0.008` (Sponza is
     * authored in centimetre-ish units): `p.local` came back identity for all 103 primitives and
     * the level was batched **125x too large** (1 x the placement's scale 2, where 0.008 x 2 was
     * meant). The observable was not the picture — it was `drawnBounds`, which read
     * **938.5 x 2558.1 x 3764.7 m** for an eighteen-metre building, 65 of the 103 primitives having
     * sailed past `BOUNDS_SKIP_RADIUS_M` and vanished from it entirely, after which
     * `checkProvenance` refused that world's baked atlas as `geometryDrift` on every boot.
     *
     * ⚠️ `mars-hangar-01` was untouched BY ACCIDENT, not by design: all 177 of its cooked GLBs are
     * a single node with no transform at all (`{"mesh":0}`), for which identity is the right
     * answer. One re-export carrying a node transform and it would have broken the same way.
     *
     * `entities/staticMesh.ts` already knows this trap and composes TRS itself
     * (`matrixRelativeTo`); its header says so in as many words. This is the SECOND reader of a
     * freshly parsed GLB, and reading `matrixWorld` is the whole reason it needs the compose that
     * the other reader sidesteps. Composing in place (rather than folding TRS per mesh) leaves the
     * shared scene's matrices TRUE for anything that reads them later, instead of routing one
     * reader around a scene that still lies. Fenced by `tests/renderStaticBatcher.test.ts`.
     */
    scene.traverse((o) => {
      if (!o.matrixAutoUpdate) o.updateMatrix();
    });
    scene.updateMatrixWorld(true);
    scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (!mat) return;
      out.push({
        geometry: batchGeometry(mesh.geometry),
        material: mat,
        // The primitive's transform RELATIVE TO THE ASSET ROOT. A GLB's meshes are rarely at its
        // origin, and dropping this puts every primitive of a multi-part asset at the placement.
        local: mesh.matrixWorld.clone(),
      });
    });
    primitives.set(assetId, out);
    return out;
  };

  const self: StaticBatcher = {
    add(assetId, scene, pose, placement = -1) {
      const prims = primitivesOf(assetId, scene);
      if (prims.length === 0) return false;

      // The placement's own transform, from the runtime's ONE definition — the same `placementQuat`
      // the renderer and the physics body use. A batcher with its own idea of where a placement is
      // would be a fourth answer to that question.
      const q = placementQuat(pose);
      const placementM = new THREE.Matrix4().compose(
        _pos.set(pose.pos[0], pose.pos[1], pose.pos[2]),
        _quat.set(q[0], q[1], q[2], q[3]),
        _scale.set(pose.scale[0], pose.scale[1], pose.scale[2]),
      );

      const at: CellPoint = { x: pose.pos[0], y: pose.pos[1], z: pose.pos[2] };

      for (let i = 0; i < prims.length; i++) {
        const p = prims[i]!;
        /*
         * ⭐ THE WINDING QUESTION IS ABOUT THE MATRIX THE VERTICES ACTUALLY GO THROUGH.
         *
         * This was `pose.scale[0] * pose.scale[1] * pose.scale[2] < 0` — the PLACEMENT's sign, which
         * is the right answer on `mars-hangar-01` only because all 177 of its cooked GLBs carry zero
         * negative-determinant nodes (measured), so `p.local` can never contribute one. The batcher
         * folds `p.local` INTO the instance matrix, so a GLB authoring a mirrored node would reach
         * neither this test nor `object.matrixWorld` — the batch would render that primitive
         * inside-out with nothing anywhere reporting it, and the unbatched path would be right
         * (there the node has its own `matrixWorld`, and three tests each mesh separately).
         *
         * `determinantAffine()` is three's own predicate, the one both backends flip the winding on
         * (`WebGPUBackend.js:2123`), so this and the renderer cannot disagree about what "mirrored"
         * means. On Mars it is the same 627 placements to the instance.
         */
        const world = new THREE.Matrix4().multiplyMatrices(placementM, p.local);
        const mirrored = world.determinantAffine() < 0;
        const groupKey = `${assetId}|${i}|${mirrored ? 'm' : ''}`;
        let group = groups.get(groupKey);
        if (!group) {
          group = {
            geometry: p.geometry,
            /*
             * ⭐ THE SAME MATERIAL OBJECT, MIRRORED OR NOT — see {@link MIRROR}. The flipped-`side`
             * clone this replaces is what turned every mirrored face black: `side = BackSide` fixes
             * the winding AND negates the shading normal, and only the first of those was wanted.
             * Sharing the material also means a mirrored batch shares its twin's WGSL and forks only
             * the PIPELINE (on `frontFaceCW`), which is one shader compile where there were two.
             */
            material: p.material,
            mirrored,
            tris: triangleCount(p.geometry),
            cellM,
            keys: new Set<string>(),
          };
          groups.set(groupKey, group);
        }
        // ⚠️ AFTER `seal()` THE GROUP'S OWN `cellM` IS THE POLICY, NOT THE AUTHORED ONE. A late
        // placement (a `summon`, an editor add) must land in the partition the level was sealed
        // with, or it becomes a batch of one beside the batch it belongs in.
        const key = `${groupKey}|${cellKeyOf(at, group.cellM)}`;
        const batch = batchFor(key, groupKey, group);
        batch.instances.push({
          /*
           * A mirrored batch's MESH carries `MIRROR`, so its instances carry `MIRROR⁻¹ · world`.
           * `MIRROR` is an involution, so this is `MIRROR · world` and the composed result is
           * `MIRROR · MIRROR · world === world` — the same place on screen, with the determinant
           * moved onto the object where three will act on it.
           */
          matrix: mirrored ? new THREE.Matrix4().multiplyMatrices(MIRROR, world) : world,
          placement,
          at,
        });
        dirty.add(key);
      }
      // Coalesced rebuild: placements land over many ticks as their GLBs arrive, and rebuilding a
      // batch per arrival is quadratic. One flush after the last add of a tick.
      if (!flushScheduled) {
        flushScheduled = true;
        queueMicrotask(() => {
          flushScheduled = false;
          self.flush();
        });
      }
      return true;
    },

    flush() {
      for (const key of dirty) {
        const batch = batches.get(key);
        if (!batch) continue;
        const need = batch.instances.length;

        /*
         * ⚠️ GROW IN PLACE. RECREATING THE MESH PER FLUSH COST 1,325 SHADER PROGRAMS.
         *
         * Placements land over hundreds of ticks as their GLBs arrive, so a batch is flushed many
         * times on the way to its final size. The first version disposed and re-created the
         * `InstancedMesh` on every one of those, and each new object is a new pipeline as far as the
         * renderer is concerned: `programs` went from 17 to **1,325** against a tier-0 budget of 24
         * (surfaceFactory rule 5 — "a jump means someone forked a variant"). Draw calls and object
         * counts both looked like wins in the same measurement, which is exactly how this would
         * have shipped.
         *
         * So the mesh is allocated ONCE with headroom and only its matrices and `count` change.
         * Capacity doubles when exceeded, so a batch reallocates O(log n) times over a level load
         * rather than once per placement.
         */
        if (batch.mesh !== null && batch.capacity >= need) {
          for (let i = 0; i < need; i++) batch.mesh.setMatrixAt(i, batch.instances[i]!.matrix);
          batch.mesh.count = need;
          batch.mesh.instanceMatrix.needsUpdate = true;
          batch.mesh.computeBoundingSphere();
          continue;
        }

        if (batch.mesh) {
          root.remove(batch.mesh);
          batch.mesh.dispose();
          meshToBatch.delete(batch.mesh);
        }
        const capacity = Math.max(8, 1 << Math.ceil(Math.log2(Math.max(1, need))));
        const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, capacity);
        batch.capacity = capacity;
        for (let i = 0; i < need; i++) mesh.setMatrixAt(i, batch.instances[i]!.matrix);
        mesh.count = need;
        mesh.instanceMatrix.needsUpdate = true;
        /*
         * ⭐ A MIRRORED BATCH SAYS SO ON ITS OWN MATRIX — the whole of {@link MIRROR}'s argument, in
         * one line. `scale.set(-1, 1, 1)` composes to `MIRROR` exactly, `matrixWorld` inherits it
         * (`root` is unmirrored), and `determinantAffine() < 0` is then true for the OBJECT, which
         * is the only thing either backend looks at. The instance matrices were pre-multiplied by
         * `MIRROR⁻¹` in `add`, so nothing moves on screen.
         */
        if (batch.mirrored) mesh.scale.copy(MIRROR_SCALE);
        /*
         * The batch is STATIC by construction — it is built from `static_mesh` placements, which
         * `EntityMeta.mobility` declares never move. So it takes the same policy the placements do:
         * composed once, never auto-updated, and its world matrix computed here rather than by the
         * per-frame walk.
         */
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        /*
         * ⭐ AND THE DECLARATION ABOVE HAS TO SURVIVE `root.add(mesh)`, WHICH IT DID NOT.
         *
         * `root` is under `entityRoot`, whose `childadded` hook runs `optInSubtree` on everything
         * added beneath it — and `optInSubtree`'s first act is `matrixAutoUpdate = true`. So the
         * policy set two lines up was reversed two lines down, on every batch, for the life of the
         * level: ~1,200 InstancedMeshes composing a matrix they will never change, on every one of
         * the four scene walks a frame does.
         *
         * The guard for exactly this already exists and this file simply never set it —
         * `optInSubtree` returns immediately on `apStatic`, which is how a static PLACEMENT wins the
         * same race when its GLB lands late. A batch is the most static object in the scene; it says
         * so in the one vocabulary the hook reads, BEFORE it is added, because the hook fires inside
         * `add()`.
         */
        mesh.userData['apStatic'] = true;
        // ⚠️ `computeBoundingSphere` on an InstancedMesh unions the INSTANCE bounds, and without it
        // three falls back to the single geometry's sphere at the origin — so a cell 300 m from the
        // origin would be culled the instant the camera is not near 0,0,0. Silent, and total.
        mesh.computeBoundingSphere();
        mesh.frustumCulled = true;
        batch.mesh = mesh;
        meshToBatch.set(mesh, batch);
        root.add(mesh);
        /*
         * ── ⭐ A BATCH IS STATIC WORLD GEOMETRY, AND IT HAS TO SAY SO *AFTER* `add` ───────────────
         *
         * `entityRoot` carries `ENTITY_SHADOW_DEFAULT` — `{cast, receive, movable: true}` — and
         * `applyShadowFlags` hooks `childadded`, so the line above stamps every batch as MOVABLE.
         * That default is right for what it was written for (pot shards, a crate parented from a
         * callback: the host cannot attribute those to a class, so they take the conservative
         * answer). It is wrong for ~1,200 `InstancedMesh`es of walls.
         *
         * ⚠️ AND `movable` IS NOT COSMETIC — IT MEANS "SKIP THIS SUBTREE WHOLE". `enableStaticShadowLayer`
         * returns immediately on the dynamic-caster mark, so with it set, NOTHING the batcher builds
         * opts into `FWG_STATIC_SHADOW_LAYER`. A light or a sun with `shadowUpdate: 'staticOnly'`
         * would freeze a map rendered from that layer and get an EMPTY one — the whole level
         * silently casting no shadow, which reads as "the sun stopped working" and gets blamed on
         * the light. `FWG_STATIC_SHADOW_LAYER`'s own note calls that failure "worse than useless".
         *
         * It is latent rather than live only because every world authors `everyFrame` today. It is
         * also the PREREQUISITE for the measured ~20 ms/frame two-map split (static world rendered
         * once, dynamic casters re-rendered per frame), so it is fixed here rather than discovered
         * by the change that depends on it.
         *
         * ⚠️ AFTER `add`, NOT BEFORE — stamping first does not survive, because the hook fires
         * INSIDE `add()` and overwrites it. The applier's `else` branch is what clears the mark.
         *
         * `ENTITY_SHADOW.prop` is the row, and not an approximation: it is what `static_mesh`
         * already declares, so a batched placement now carries exactly the flags it would have
         * carried unbatched, through the host's own create-diff.
         */
        applyShadowFlags(mesh, ENTITY_SHADOW.prop);
        mesh.updateMatrixWorld(true);
      }
      dirty.clear();
    },

    seal() {
      sealed = true;
      let moved = 0;
      let before = 0;
      let after = 0;
      for (const [groupKey, group] of groups) {
        // Every instance the group holds, in the order it was added — which is the order `pick`
        // maps back through, so the re-partition must preserve it WITHIN each new batch.
        const all: Instance[] = [];
        for (const key of group.keys) {
          const batch = batches.get(key);
          if (batch) all.push(...batch.instances);
        }
        before += group.keys.size;
        if (all.length === 0) continue;
        const priced = priceCellM(
          all.map((i) => i.at),
          group.tris,
          cellM,
          minCullTris,
        );
        if (priced === group.cellM) {
          after += group.keys.size;
          continue;
        }

        // Tear the group's batches down — the meshes, never the shared geometry or material.
        for (const key of group.keys) {
          const batch = batches.get(key);
          if (batch?.mesh) {
            root.remove(batch.mesh);
            batch.mesh.dispose();
            meshToBatch.delete(batch.mesh);
          }
          batches.delete(key);
          dirty.delete(key);
        }
        group.keys.clear();
        group.cellM = priced;
        for (const inst of all) {
          const key = `${groupKey}|${cellKeyOf(inst.at, priced)}`;
          batchFor(key, groupKey, group).instances.push(inst);
          dirty.add(key);
        }
        moved += 1;
        after += group.keys.size;
      }
      if (moved > 0) {
        console.log(
          `[batch] sealed: ${groups.size} group(s), ${moved} re-priced — ${before} batch(es) -> ${after} ` +
            `(base cell ${Number.isFinite(cellM) ? `${cellM} m` : 'none'}, min ${minCullTris} tris to be worth culling)`,
        );
      }
    },

    pick(mesh, instanceId) {
      const batch = meshToBatch.get(mesh);
      if (!batch) return null;
      return batch.instances[instanceId]?.placement ?? null;
    },

    clear() {
      for (const batch of batches.values()) {
        if (!batch.mesh) continue;
        root.remove(batch.mesh);
        batch.mesh.dispose(); // the InstancedMesh's instance buffer only — NOT the shared geometry
      }
      /*
       * ⚠️ NOTHING TO DISPOSE HERE ANY MORE, AND THAT IS THE POINT. This used to free the mirrored
       * MATERIAL CLONES — a per-(asset, primitive) twin whose only difference was a flipped `side`.
       * Since {@link MIRROR} the mirror is on the batch's own matrix and a mirrored batch shares its
       * twin's material object, which belongs to the asset and is disposed with it. A `dispose()`
       * here would free a material the unmirrored batches are still drawing with.
       */
      batches.clear();
      // The groups go with the batches — a group surviving a `clear()` would keep a sealed cell
      // size, and its geometry/material references, across a world it no longer belongs to.
      groups.clear();
      primitives.clear();
      dirty.clear();
      sealed = false;
    },

    stats() {
      let instances = 0;
      for (const b of batches.values()) instances += b.instances.length;
      return { batches: batches.size, instances, assets: primitives.size, sealed };
    },
  };
  return self;
}
